import React, { useContext, useEffect, useMemo, useState } from "react";
import { MdChevronRight, MdLogout, MdNotifications, MdSearch } from "react-icons/md";
import { LayoutGrid, Plus, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import styles from "././Styles/Home.module.css";
import ViewNotification from "./Components/Notifications/ViewNotification";
import AddNewFollowup from "./Components/Followup/AddNewFollowup";
import { BASE_URL_API } from "./References/urls";
import AuthContext from "./store/auth-context";

const HOME_UI_STORAGE_KEY = "home-ui-version";
const NAV_SHORTCUTS = [
    { to: "", label: "Home" },
    { to: "/performance-summary", label: "Summary" },
    { to: "/tasks-router", label: "Tasks" },
    { to: "/training", label: "Trainings" },
    { to: "/analytics/recruiter", label: "Recruiter Analytics" },
    { to: "/caller", label: "Caller" },
    { to: "/follow-ups", label: "Follow Ups" },
    { to: "/kpis", label: "KPIs", adminOnly: true },
];

const Home = () => {
    const authCtx = useContext(AuthContext);
    authCtx.token === "" && authCtx.logout();
    const [isNotificationOpen, setIsNotificationOpen] = useState(false)
    const [isAddNewFollowupOpen, setIsAddNewFollowupOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState("");
    const [isModernView, setIsModernView] = useState(() => {
        const persistedView = localStorage.getItem(HOME_UI_STORAGE_KEY);
        return persistedView !== "legacy";
    });

    const getCategoryOptions = async () => {
        const getCategoryOptionsResponse = await fetch(BASE_URL_API + "/getCategoryOptions",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token
                })
            });
        if (!getCategoryOptionsResponse.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const getCategoryOptionsRespo = await getCategoryOptionsResponse.json();
            if (getCategoryOptionsRespo.status === "success") {
                console.log("Login Verified");
            } else {
                if (getCategoryOptionsRespo.message === "token Expired, please login again!") {
                    authCtx.logout();
                } else {
                    console.log("Something went wrong!!!")
                }
            }
        }
    }

    const options = [
        {
            "header": "Academics",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "academics/visitors",
                    "title": "Visitors"
                },
                {
                    "navigateTo": "academics/enquires",
                    "title": "Enquires"
                },
                {
                    "navigateTo": "academics/enquires/calendar",
                    "title": "Scheduled Visits"
                },
                {
                    "navigateTo": "academics/admissions",
                    "title": "Registrations / Admissions"
                },
            ]
        },
        {
            "header": "Analytics",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "analytics/recruiter",
                    "title": "Recruiter Analytics"
                },
                {
                    "navigateTo": "analytics/job",
                    "title": "Job Analytics"
                },
                {
                    "navigateTo": "analytics/partner",
                    "title": "Partner Analytics"
                },
                {
                    "navigateTo": "analytics/app-user",
                    "title": "App User Analytics"
                }
            ]
        },
        {
            "header": "Applications",
            "children": [
                {
                    "navigateTo": "manage-applications",
                    "title": "Applications"
                },
                {
                    "navigateTo": "create-applications",
                    "title": "Create Applications"
                },
                {
                    "navigateTo": "application-calendar",
                    "title": "Application Calendar"
                },
                {
                    "navigateTo": "interview-tracker",
                    "title": "Interview Tracker"
                },
            ]
        },
        {
            "header": "Campaigns",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "campaigns",
                    "title": "All Campaigns"
                }
            ]
        },
        {
            "header": "Fast Track",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "ft-all-partners",
                    "title": "All Academic Partners"
                },
                {
                    "navigateTo": "ft-placement-drives",
                    "title": "All Placement Drives"
                },
            ]
        },
        {
            "header": "Invoices",
            "children": [
                {
                    "navigateTo": "all-hiring-invoices",
                    "title": "Partners Invoices"
                },
                {
                    "navigateTo": "students-invoices",
                    "title": "Students Invoices"
                },
            ]
        },
        {
            "header": "Clients",
            "children": [
                {
                    "navigateTo": "assign-clients",
                    "title": "Assign Clients"
                },
                {
                    "navigateTo": "manage-clients-home",
                    "title": "Manage Clients"
                },
                {
                    "navigateTo": "manage-business-accounts",
                    "title": "Manage Business Account"
                },
                {
                    "navigateTo": "create-service-agreement",
                    "title": "Create Service Agreements"
                },
                {
                    "navigateTo": "manage-service-agreements",
                    "title": "Manage Service Agreements"
                }

            ]
        },
        {
            "header": "Courses",
            "children": [
                {
                    "navigateTo": "academics/courses",
                    "title": "Academic Courses"
                },
                {
                    "navigateTo": "manage-course",
                    "title": "Online Courses"
                }
            ]
        },
        {
            "header": "Cases",
            "children": [
                {
                    "navigateTo": "escalated-case/jobs/null",
                    "title": "Escalated Job Cases"
                }
            ]
        },
        {
            "header": "Jobs",
            "children": [
                {
                    "navigateTo": "post-job",
                    "title": "Post Job"
                },
                {
                    "navigateTo": "manage-jobs",
                    "title": "Manage Job"
                },
                {
                    "navigateTo": "jobs-allocator",
                    "title": "Allot Job"
                },
                {
                    "navigateTo": "jobs-tracker",
                    "title": "Jobs Tracker"
                },
                {
                    "navigateTo": "marketing/generate-job-route",
                    "title": "Job Routes",
                    "adminOnly": true
                },
            ]
        },
        {
            "header": "Instructors",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "instructors",
                    "title": "Manage Instructors"
                },
                {
                    "navigateTo": "add-instructor",
                    "title": "Add Instructor"
                }
            ]
        },
        {
            "header" : "Notification Engine",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "notifications",
                    "title": "Send Notification"
                },
                {
                    "navigateTo": "notification-logs",
                    "title": "Notification Logs"
                }
            ]
        },
        {
            "header": "Partners",
            "children": [
                {
                    "navigateTo": "manage-partners-account/search",
                    "title": "Create New Account"
                },
                {
                    "navigateTo": "manage-partners-account",
                    "title": "Manage Partner's Account"
                },
                {
                    "navigateTo": "manage-stores",
                    "title": "Manage Stores"
                },
                {
                    "navigateTo": "salon-leads",
                    "title": "Salons Leads"
                }
            ]
        },
        {
            "header": "Posts",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "manage-posts",
                    "title": "Manage Posts"
                }
            ]
        },
        {
            "header": "Users",
            "children": [
                {
                    "navigateTo": "manage-users",
                    "title": "Manage Users"
                }
            ]
        },
        {
            "header": "Workshops",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "workshops",
                    "title": "Manage Workshops"
                },
                {
                    "navigateTo": "create-workshop",
                    "title": "Create Workshop"
                },
                {
                    "navigateTo": "workshop-registrations",
                    "title": "Workshop Registrations"
                },
                {
                    "navigateTo": "workshop-closure-report",
                    "title": "Update Closure Reports"
                }
            ]
        },
        {
            "header": "Others",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "manage-skillsets",
                    "title": "Manage Skill Sets"
                }
            ]
        },
        {
            "header": "Leads Manager",
            "children": [
                {
                    "navigateTo": "leads-feeder",
                    "title": "Leads Feeder"
                },
                {
                    "navigateTo": "leads-distributor",
                    "title": "Leads Distributor"
                },
                {
                    "navigateTo": "salon-leads-feeder",
                    "title": "Salon Leads Feeder"
                },
                {
                    "navigateTo": "salon-leads-distributor",
                    "title": "Salon Leads Distributor"
                },
                {
                    "navigateTo": "lead-filteration",
                    "title": "Course Leads Filteration"
                },
                {
                    "navigateTo": "course-leads-distributor",
                    "title": "Course Leads Distributor"
                },
            ]
        },
        {
            "header": "Administration",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "admin/manage-demographics",
                    "title": "Manage Demographics"
                },
                {
                    "navigateTo": "admin/manage-locations",
                    "title": "Manage Locations & Areas"
                },
            ]
        },
        {
            "header": "Referral Management",
            "children": [
                {
                    "navigateTo": "/referral-management/queue",
                    "title": "Verification Queue"
                },
                {
                    "navigateTo": "/referral-management/queue/withdrawal",
                    "title": "Withdrawal Queue"
                },
                {
                    "navigateTo": "/referral-management/queue/fraud",
                    "title": "Fraud Queue"
                },
                {
                    "navigateTo": "/referral-management/configuration",
                    "title": "Configuration",
                    "adminOnly": true
                }
            ]
        },
        {
            "header": "Raw Data",
            "adminOnly": true,
            "children": [
                {
                    "navigateTo": "data/manage-academies",
                    "title": "Manage Academies Data"
                },
            ]
        },
    ]


    useEffect(() => {
        getCategoryOptions();
    }, [])

    useEffect(() => {
        localStorage.setItem(HOME_UI_STORAGE_KEY, isModernView ? "modern" : "legacy");
    }, [isModernView]);

    const isAdmin = authCtx.userIdentifier === '999964N8406';
    const visibleShortcuts = NAV_SHORTCUTS.filter(s => !s.adminOnly || isAdmin);
    const visibleOptions = options
        .filter(section => !section.adminOnly || isAdmin)
        .map(section => ({
            ...section,
            children: section.children.filter(child => !child.adminOnly || isAdmin),
        }))
        .filter(section => section.children.length > 0);

    const normalizedSearchQuery = String(searchQuery || "").trim().toLowerCase();

    const filteredOptions = useMemo(() => {
        if (!normalizedSearchQuery) return visibleOptions;

        return visibleOptions
            .map((section) => {
                const isHeaderMatch = section.header.toLowerCase().includes(normalizedSearchQuery);
                if (isHeaderMatch) return section;

                const filteredChildren = section.children.filter((child) =>
                    child.title.toLowerCase().includes(normalizedSearchQuery)
                );

                return {
                    ...section,
                    children: filteredChildren,
                };
            })
            .filter((section) => section.children.length > 0);
    }, [normalizedSearchQuery, visibleOptions]);

    const ActionButtons = ({ extraClassName = "" }) => (
        <div className={`${styles.headerNavigationRight} ${extraClassName}`.trim()}>
            <button
                onClick={() => setIsModernView((prev) => !prev)}
                className={styles.viewToggleBtn}
                type="button"
            >
                {isModernView ? "Classic View" : "New View"}
            </button>
            <Link to="/global-search" className={styles.gsIcon}>
                <MdSearch size={22} />
            </Link>
            <button
                onClick={() => setIsAddNewFollowupOpen(true)}
                className={styles.addFollowupBtn}
                type="button"
                title="Add New Follow-up"
                aria-label="Add New Follow-up"
            >
                <Plus size={15} />
            </button>
            <button onClick={() => setIsNotificationOpen(true)} className={styles.notificationBtn} type="button">
                <MdNotifications size={22} />
            </button>
            <button onClick={() => authCtx.logout()} className={styles.logoutBtn} type="button">
                <MdLogout />
            </button>
        </div>
    );

    const renderLegacyView = () => (
        <div className={styles.mainContainer}>
            <div className={styles.header}>
                <div className={styles.headerNavigation}>
                    {visibleShortcuts.map((item) => (
                        <Link to={item.to} key={item.label}>
                            <div className={styles.eachHeaderNavigation}>{item.label}</div>
                        </Link>
                    ))}
                </div>
                <ActionButtons />
            </div>
            <div className={styles.optionContainer}>
                {visibleOptions.map((item, index) => (
                    <div className={styles.eachOption} key={index + "BUS"}>
                        <div className={styles.eachOptionHeader}>{item.header}</div>
                        {item.children.map((itemChild, indexChild) => (
                            <Link
                                to={itemChild.navigateTo}
                                style={{ textDecoration: "none" }}
                                key={index + "O" + indexChild}
                            >
                                <div className={styles.optionChild} key={indexChild}>
                                    <div className={styles.optionIcon}></div>
                                    <div className={styles.optionTitle}>{itemChild.title}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderModernView = () => (
        <div className={styles.modernContainer}>
            <div className={styles.modernHeader}>
                <div className={styles.modernHeaderNavigation}>
                    {visibleShortcuts.map((item) => (
                        <Link key={item.label} to={item.to} className={styles.modernHeaderNavItem}>
                            {item.label}
                        </Link>
                    ))}
                </div>
                <ActionButtons extraClassName={styles.modernHeaderActions} />
            </div>

            <div className={styles.modernToolbar}>
                <div className={styles.modernToolbarTitle}><LayoutGrid size={16} /> Browse Modules</div>
                <div className={styles.modernSearchWrap}>
                    <MdSearch size={16} />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Search module or page"
                    />
                </div>
            </div>

            <div className={styles.modernCardsGrid}>
                {filteredOptions.map((section) => (
                    <section key={section.header} className={styles.modernSectionCard}>
                        <div className={styles.modernSectionHeader}>
                            <span className={styles.modernSectionHeaderTitle}><Sparkles size={13} /> {section.header}</span>
                        </div>
                        <div className={styles.modernSectionLinks}>
                            {section.children.map((child) => (
                                <Link key={`${section.header}-${child.navigateTo}`} to={child.navigateTo} className={styles.modernRouteLink}>
                                    <span className={styles.modernRouteText}>{child.title}</span>
                                    <span className={styles.modernRouteArrow}><MdChevronRight size={16} /></span>
                                </Link>
                            ))}
                        </div>
                    </section>
                ))}
            </div>
        </div>
    );


    return (
        <>
            {isNotificationOpen === true && <ViewNotification isOpen={isNotificationOpen} onClose={() => setIsNotificationOpen(false)} />}
            {isAddNewFollowupOpen && <AddNewFollowup
                isOpen={isAddNewFollowupOpen}
                onClose={() => setIsAddNewFollowupOpen(false)}
                onAdd={() => setIsAddNewFollowupOpen(false)}
            />}
            {isModernView ? renderModernView() : renderLegacyView()}
        </>
    )
}

export default Home;